// @flow

// scrollUtils are a set of functions that help in performing scroll operations.
// This includes measurement, animation, easing, and a few other helpful utils.

import createAnimationManager from 'app/common/lib/react/animationUtils';

export type EASING_FUNCTION_NAME = 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out';

export type ScrollContainerOptions = {
  easingFunction?: EASING_FUNCTION_NAME,

  extendToEdgeDistanceX?: number,
  extendToEdgeDistanceY?: number,

  scrollSpeedPxPerMs?: number,
  minScrollDuration?: number,

  onAnimationComplete?: () => void,
};

type getScrollBoundsParams = {
  scrollPosition: number,
  containerSize: number,
  contentSize: number,
  intendedDistance: number,
  extendToEdgeDistance?: number,
};

const DEFAULT_SCROLL_CONTAINER_OPTIONS: ScrollContainerOptions = {
  easingFunction: 'ease-in-out',
  extendToEdgeDistanceX: 0,
  extendToEdgeDistanceY: 0,
  scrollSpeedPxPerMs: 1.2,
  minScrollDuration: 200,
};

const EASING_FUNCTIONS: { [key: EASING_FUNCTION_NAME]: (t: number) => number } = {
  linear: (t) => t,
  'ease-in': (t) => t * t,
  'ease-out': (t) => t * (2 - t),
  'ease-in-out': (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
};

// Based on the current size and scroll position of the container element, bound
// the intended scroll distance. If the new scroll position would be near the
// min or max, see if we should bump it out to the edge based on the
// extendToEdgeDistance.
export const getScrollBounds = ({
  scrollPosition,
  containerSize,
  contentSize,
  intendedDistance,
  extendToEdgeDistance = 0,
}: getScrollBoundsParams) => {
  const maxScrollPosition = contentSize - containerSize;
  const maxScrollDistance = maxScrollPosition - scrollPosition;
  const minScrollDistance = -scrollPosition;

  let scrollDistance = intendedDistance;

  scrollDistance = Math.min(scrollDistance, maxScrollDistance);
  scrollDistance = Math.max(scrollDistance, minScrollDistance);

  if (
    intendedDistance > 0 &&
    maxScrollPosition - (scrollPosition + scrollDistance) <= extendToEdgeDistance
  ) {
    scrollDistance = maxScrollPosition - scrollPosition;
  } else if (intendedDistance < 0 && scrollPosition + scrollDistance <= extendToEdgeDistance) {
    scrollDistance = -scrollPosition;
  }

  return scrollDistance;
};

// Given a container and a description of how you'd like it to scroll,
// getScrollAnimation returns an animation object from createAnimationManager.
export const getScrollAnimation = (
  container: HTMLElement,
  intendedScrollX: number,
  intendedScrollY: number,
  options?: ScrollContainerOptions = {},
) => {
  const mergedOptions = { ...DEFAULT_SCROLL_CONTAINER_OPTIONS, ...options };
  const {
    scrollLeft: initialScrollLeft,
    scrollTop: initialScrollTop,
    scrollWidth,
    scrollHeight,
    clientWidth,
    clientHeight,
  } = container;

  // Get the bounded distance.
  const scrollX = getScrollBounds({
    scrollPosition: initialScrollLeft,
    containerSize: clientWidth,
    contentSize: scrollWidth,
    intendedDistance: intendedScrollX,
    extendToEdgeDistance: mergedOptions.extendToEdgeDistanceX,
  });
  const scrollY = getScrollBounds({
    scrollPosition: initialScrollTop,
    containerSize: clientHeight,
    contentSize: scrollHeight,
    intendedDistance: intendedScrollY,
    extendToEdgeDistance: mergedOptions.extendToEdgeDistanceY,
  });

  // Get the bounded duration.
  const durationX = Math.abs(Math.floor(scrollX / mergedOptions.scrollSpeedPxPerMs));
  const durationY = Math.abs(Math.floor(scrollY / mergedOptions.scrollSpeedPxPerMs));
  let duration = Math.max(durationX, durationY);

  if (mergedOptions.minScrollDuration) {
    duration = Math.max(duration, mergedOptions.minScrollDuration);
  }

  // Get the easing function.
  const easingFunction =
    EASING_FUNCTIONS[mergedOptions.easingFunction] || EASING_FUNCTIONS['ease-in-out'];

  // Create the animation.
  const scrollAnimation = createAnimationManager({
    window,
    duration,
    run: (progress) => {
      container.scrollLeft = initialScrollLeft + scrollX * easingFunction(progress);
      container.scrollTop = initialScrollTop + scrollY * easingFunction(progress);

      if (progress === 1 && mergedOptions.onAnimationComplete) {
        setTimeout(mergedOptions.onAnimationComplete, 0);
      }
    },
  });

  return scrollAnimation;
};

// The following scroll utils have been copied from Gestalt's scrollUtils which,
// at the time of this development, is not exported.

export function getElementHeight(element: HTMLElement): number {
  return element === window ? window.innerHeight : element.clientHeight;
}

export function getWindowScrollPos() {
  if (window.scrollY !== undefined) {
    // Modern browser
    return window.scrollY;
  } else if (document.documentElement && document.documentElement.scrollTop !== undefined) {
    // IE support.
    return document.documentElement.scrollTop;
  }
  return 0;
}

export function getRelativeScrollTop(element: HTMLElement): number {
  return element === window
    ? getWindowScrollPos()
    : element.scrollTop - element.getBoundingClientRect().top;
}

export function getScrollHeight(element: HTMLElement): number {
  return element === window && document.documentElement
    ? document.documentElement.scrollHeight
    : element.scrollHeight;
}

export function getScrollPos(element: HTMLElement): number {
  return element === window ? getWindowScrollPos() : element.scrollTop;
}

export function scrollToTopOfPage() {
  if (window) {
    window.scroll(0, 0);
  }
}
